// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/pages/services/tarallo/TileArt.tsx
// 🔧 PURPOSE: Le figure delle tessere di "Dal tarallo al trullo", disegnate
//   in SVG (uguali su ogni telefono, niente emoji):
//   2 tarallo · 4 oliva · 8 orecchietta · 16 fico · 32 limone · 64 burrata ·
//   128 focaccia · 256 panzerotto · 512 polpo di Mola · 1024 gozzo ·
//   2048 trullo · 4096 castello · oltre: il sole.
//   Ogni livello ha anche il suo fondo (TILE_STYLES).
// ============================================

import React from 'react';

export const TILE_LEVELS = [2, 4, 8, 16, 32, 64, 128, 256, 512, 1024, 2048, 4096] as const;

/** La chiave del nome (games.tarallo.tiles.<chiave>) di ogni livello. */
export const TILE_NAME_KEYS: Record<number, string> = {
  2: 'tarallo', 4: 'olive', 8: 'orecchietta', 16: 'fig', 32: 'lemon', 64: 'burrata',
  128: 'focaccia', 256: 'panzerotto', 512: 'octopus', 1024: 'boat', 2048: 'trullo', 4096: 'castle',
};
export const tileNameKey = (value: number) => TILE_NAME_KEYS[value] || 'sun';

/** Fondo e colore del numero, dal crema dei primi al blu notte degli ultimi. */
export const TILE_STYLES: Record<number, { bg: string; fg: string }> = {
  2: { bg: '#F7EEDC', fg: '#7A5E1E' },
  4: { bg: '#EFE3C4', fg: '#6B5418' },
  8: { bg: '#F4D9A6', fg: '#6B4A12' },
  16: { bg: '#EBC3C8', fg: '#7A2B3A' },
  32: { bg: '#F7E68C', fg: '#6B5A0A' },
  64: { bg: '#E8F0F2', fg: '#3D5A66' },
  128: { bg: '#F0C27A', fg: '#6B3E08' },
  256: { bg: '#E9A866', fg: '#5C2E05' },
  512: { bg: '#D9A3C9', fg: '#5A1F4A' },
  1024: { bg: '#9CC7E3', fg: '#123E5C' },
  2048: { bg: '#C4A24C', fg: '#2A2620' },
  4096: { bg: '#3B3327', fg: '#E6CC85' },
};
export const tileStyle = (value: number) => TILE_STYLES[value] || { bg: '#1E3A5F', fg: '#F7E68C' };

const Svg: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg viewBox="0 0 48 48" width="100%" height="100%" aria-hidden="true" focusable="false">
    {children}
  </svg>
);

const ART: Record<string, React.ReactNode> = {
  tarallo: (
    <Svg>
      <circle cx="24" cy="25" r="13" fill="none" stroke="#D9A85B" strokeWidth="7" />
      <circle cx="24" cy="25" r="13" fill="none" stroke="#B9823A" strokeWidth="1.6" strokeDasharray="5 4" />
      <path d="M14 18c4-4 8-5 13-4" stroke="#F3D49B" strokeWidth="2" fill="none" strokeLinecap="round" />
    </Svg>
  ),
  olive: (
    <Svg>
      <path d="M30 12c4-5 10-5 12-3-3 4-8 6-12 3z" fill="#5F7F2E" />
      <ellipse cx="22" cy="28" rx="11" ry="14" transform="rotate(-25 22 28)" fill="#6E8B2F" />
      <ellipse cx="18" cy="23" rx="3" ry="5" transform="rotate(-25 18 23)" fill="#A7C060" opacity=".7" />
      <path d="M28 15l3-4" stroke="#4B3A1E" strokeWidth="2" strokeLinecap="round" />
    </Svg>
  ),
  orecchietta: (
    <Svg>
      <ellipse cx="24" cy="25" rx="15" ry="13" fill="#F0CF8E" />
      <ellipse cx="24" cy="26" rx="9" ry="7.5" fill="#D9AE62" />
      <ellipse cx="22" cy="24" rx="5" ry="3.5" fill="#C99A4E" />
      <path d="M11 22c3-6 9-9 15-9" stroke="#FBE6B8" strokeWidth="2" fill="none" strokeLinecap="round" />
    </Svg>
  ),
  fig: (
    <Svg>
      <path d="M24 9c2 3 2 5 1 7" stroke="#4E6B23" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <path d="M26 14c3-2 7-2 9 0-3 2-6 2-9 0z" fill="#6E8B2F" />
      <path d="M24 15c-9 0-14 10-12 18 2 7 22 7 24 0 2-8-3-18-12-18z" fill="#7B3F6E" />
      <path d="M18 22c-2 3-2 7 0 10" stroke="#A86A97" strokeWidth="2" fill="none" strokeLinecap="round" />
    </Svg>
  ),
  lemon: (
    <Svg>
      <path d="M30 11c3-4 9-4 11-2-2 4-7 5-11 2z" fill="#4E7A2A" />
      <path d="M8 27c2-9 11-15 20-13 8 2 13 9 12 16-2 9-12 12-20 10C12 38 7 33 8 27z" fill="#F5D33C" />
      <path d="M8 27l-3 1 3 1zM40 30l3 0-3-2z" fill="#E8C11F" />
      <ellipse cx="20" cy="22" rx="5" ry="2.5" transform="rotate(-20 20 22)" fill="#FBEA8F" />
    </Svg>
  ),
  burrata: (
    <Svg>
      <path d="M24 16c-10 0-15 8-14 15 1 7 9 9 14 9s13-2 14-9c1-7-4-15-14-15z" fill="#FFFFFF" stroke="#D6DEE2" strokeWidth="1.5" />
      <path d="M19 15c2-4 8-4 10 0l-2 3h-6z" fill="#F4F7F8" stroke="#D6DEE2" strokeWidth="1.5" />
      <path d="M20 17h8" stroke="#6E8B2F" strokeWidth="2.5" strokeLinecap="round" />
      <ellipse cx="18" cy="27" rx="4" ry="2.5" fill="#EEF3F5" />
    </Svg>
  ),
  focaccia: (
    <Svg>
      <rect x="8" y="12" width="32" height="25" rx="7" fill="#E3A94E" />
      <rect x="10" y="14" width="28" height="21" rx="6" fill="#EDBE68" />
      <circle cx="17" cy="20" r="3" fill="#D8412F" />
      <circle cx="30" cy="22" r="3" fill="#D8412F" />
      <circle cx="22" cy="29" r="3" fill="#D8412F" />
      <circle cx="25" cy="19" r="1.8" fill="#4E5D2A" />
      <circle cx="15" cy="29" r="1.8" fill="#4E5D2A" />
      <circle cx="32" cy="30" r="1.8" fill="#4E5D2A" />
    </Svg>
  ),
  panzerotto: (
    <Svg>
      <path d="M7 31c0-11 8-19 17-19s17 8 17 19z" fill="#E09A45" />
      <path d="M7 31h34" stroke="#B9702A" strokeWidth="3" strokeLinecap="round" strokeDasharray="1 3.5" />
      <path d="M13 23c3-5 7-7 12-7" stroke="#F3C27A" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    </Svg>
  ),
  octopus: (
    <Svg>
      <path d="M24 8c-8 0-12 6-12 12 0 4 2 6 4 8h16c2-2 4-4 4-8 0-6-4-12-12-12z" fill="#B5487A" />
      <circle cx="20" cy="19" r="2.2" fill="#2A2620" />
      <circle cx="28" cy="19" r="2.2" fill="#2A2620" />
      <path d="M16 28c-2 4-6 6-8 5M21 28c-1 5-3 9-6 10M27 28c1 5 3 9 6 10M32 28c2 4 6 6 8 5" stroke="#B5487A" strokeWidth="3.4" fill="none" strokeLinecap="round" />
    </Svg>
  ),
  boat: (
    <Svg>
      <path d="M23 9v20" stroke="#5C4A2E" strokeWidth="2" />
      <path d="M24 10l11 16H24z" fill="#F7F3EA" stroke="#D8CFBF" strokeWidth="1" />
      <path d="M6 29h36l-5 9H11z" fill="#FFFFFF" stroke="#C9D6DE" strokeWidth="1" />
      <path d="M8 32h32" stroke="#1F6FA8" strokeWidth="3" />
      <path d="M6 29h36" stroke="#C0392B" strokeWidth="2" />
      <path d="M4 42c4-2 6 2 10 0s6 2 10 0 6 2 10 0 6 2 10 0" stroke="#3E8EC4" strokeWidth="2" fill="none" />
    </Svg>
  ),
  trullo: (
    <Svg>
      <path d="M24 5l2 4h-4z" fill="#FFFFFF" />
      <circle cx="24" cy="5" r="2" fill="#FFFFFF" />
      <path d="M24 9L11 29h26z" fill="#8C8577" />
      <path d="M18 19h12M15 24h18" stroke="#6F695D" strokeWidth="1.4" />
      <rect x="10" y="28" width="28" height="13" rx="1.5" fill="#FFFFFF" />
      <path d="M21 41v-7a3 3 0 016 0v7z" fill="#5C4A2E" />
      <path d="M24 13l-1.5 4h3z" fill="#FFFFFF" opacity=".9" />
    </Svg>
  ),
  castle: (
    <Svg>
      <path d="M10 18h5v-4h4v4h3v-4h4v4h3v-4h4v4h5v23H10z" fill="#D8C9A8" />
      <path d="M10 18h28" stroke="#B8A47C" strokeWidth="1.5" />
      <path d="M21 41v-8a3 3 0 016 0v8z" fill="#5C4A2E" />
      <rect x="15" y="24" width="3" height="5" fill="#8C7A55" />
      <rect x="30" y="24" width="3" height="5" fill="#8C7A55" />
    </Svg>
  ),
  sun: (
    <Svg>
      <circle cx="24" cy="24" r="9" fill="#F7D24A" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <path key={a} d="M24 6v6" stroke="#F7D24A" strokeWidth="3" strokeLinecap="round" transform={`rotate(${a} 24 24)`} />
      ))}
    </Svg>
  ),
};

export const TileArt: React.FC<{ value: number }> = ({ value }) => <>{ART[tileNameKey(value)]}</>;

export default TileArt;
